<script lang="ts">
  import { onMount } from "svelte";

  export let goldCount: number = 0;
  export let silverCount: number = 0;
  export let bronzeCount: number = 0;
  export let revealed: boolean = false;
  export let useMobileLayout: boolean = false;

  // Derived values
  $: total = Math.max(0, (goldCount ?? 0) + (silverCount ?? 0) + (bronzeCount ?? 0));
  $: gold = Math.max(0, goldCount ?? 0);
  $: silver = Math.max(0, silverCount ?? 0);
  $: bronze = Math.max(0, bronzeCount ?? 0);
  $: largestLabel = gold >= silver && gold >= bronze ? "gold" : silver >= gold && silver >= bronze ? "silver" : "bronze";

  // Animated progress (0 -> 1) for donut fill
  let progress = 0;
  let reduceMotion = false;

  // Animated counters
  let displayGold = 0;
  let displaySilver = 0;
  let displayBronze = 0;

  function easeOutQuad(t: number) { return 1 - (1 - t) * (1 - t); }

  function animateValue(target: number, setter: (v: number) => void, baseDuration = 900) {
    const capped = Math.min(1500, baseDuration + Math.min(1000, target * 30));
    const start = performance.now();
    const startVal = 0;
    function frame(now: number) {
      const elapsed = now - start;
      const t = Math.max(0, Math.min(1, elapsed / capped));
      const val = Math.round(startVal + easeOutQuad(t) * target);
      setter(val);
      if (t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  function animateDonut(duration = 800) {
    if (reduceMotion) { progress = 1; return; }
    const start = performance.now();
    function frame(now: number) {
      const t = Math.max(0, Math.min(1, (now - start) / duration));
      progress = easeOutQuad(t);
      if (t < 1) requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  // Confetti control
  let showConfetti = false;
  let confettiTimeout: any = null;
  function triggerConfetti() {
    if (reduceMotion) return;
    showConfetti = true;
    clearTimeout(confettiTimeout);
    confettiTimeout = setTimeout(() => { showConfetti = false; }, 900);
  }

  // Watch for reveal
  $: if (revealed) {
    animateDonut();
    animateValue(gold, (v) => displayGold = v);
    animateValue(silver, (v) => displaySilver = v);
    animateValue(bronze, (v) => displayBronze = v);
    triggerConfetti();
  } else {
    progress = 0;
    displayGold = displaySilver = displayBronze = 0;
    showConfetti = false;
  }

  onMount(() => {
    try {
      reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch {}
  });

  // Donut gradient stops (animated by progress)
  $: goldShare = total === 0 ? 0 : gold / total;
  $: silverShare = total === 0 ? 0 : silver / total;
  $: bronzeShare = total === 0 ? 0 : bronze / total;
  $: goldDeg = Math.round(360 * goldShare * progress);
  $: silverDeg = Math.round(360 * silverShare * progress);
  $: bronzeDeg = Math.round(360 * bronzeShare * progress);
  $: gEnd = goldDeg; // end angle for gold segment
  $: sEnd = goldDeg + silverDeg; // end angle for silver segment
  $: bEnd = goldDeg + silverDeg + bronzeDeg; // end angle for bronze segment (<=360)

  $: donutStyle = `conic-gradient(#FFD700 0deg ${gEnd}deg, #C0C0C0 ${gEnd}deg ${sEnd}deg, #CD7F32 ${sEnd}deg ${Math.max(sEnd, bEnd)}deg, #ffffff20 ${Math.max(sEnd, bEnd)}deg 360deg)`;

  // hover tilt
  let tiltEl: HTMLElement;
  function tilt(e: MouseEvent) {
    if (useMobileLayout) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width; // 0..1
    const y = (e.clientY - rect.top) / rect.height; // 0..1
    const rx = (0.5 - y) * 8; // rotateX
    const ry = (x - 0.5) * 8; // rotateY
    tiltEl.style.transform = `perspective(600px) rotateX(${rx}deg) rotateY(${ry}deg)`;
  }
  function resetTilt() {
    if (tiltEl) tiltEl.style.transform = `perspective(600px) rotateX(0deg) rotateY(0deg)`;
  }

  // Confetti model
  type Confetti = { x: number; y: number; dx: number; dy: number; rot: number; h: number; s: number };
  let confettiPieces: Confetti[] = [];
  function makeConfetti(n = 18) {
    const arr: Confetti[] = [];
    for (let i = 0; i < n; i++) {
      arr.push({
        x: 50,
        y: 40,
        dx: (Math.random() - 0.5) * 140,
        dy: (Math.random() - 0.2) * 160,
        rot: Math.random() * 360,
        h: Math.floor(Math.random() * 360),
        s: 70 + Math.floor(Math.random() * 30),
      });
    }
    return arr;
  }
  $: if (revealed && !reduceMotion) confettiPieces = makeConfetti();
</script>

<div class="relative w-[300px] max-h-[500px] rounded-xl overflow-hidden select-none"
  aria-label="Breakthroughs summary">
  <!-- Aurora + starfield background -->
  <div class="absolute inset-0">
    <div class="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,_rgba(94,230,232,0.25),_transparent_60%),_radial-gradient(ellipse_at_bottom_right,_rgba(255,215,0,0.18),_transparent_60%)]"></div>
    <div class="absolute inset-0 bg-[linear-gradient(135deg,_rgba(0,0,0,0.4),_transparent_40%),_linear-gradient(45deg,_rgba(0,0,0,0.35),_transparent_40%)]"></div>
    <div class="absolute inset-0 vignette"></div>
    <div class="absolute inset-0 stars"></div>
  </div>

  <!-- Content wrapper with hover tilt on desktop -->
  <div class="relative z-10 p-4 flex flex-col gap-3" role="group"
    on:mousemove={(e) => tilt(e)} on:mouseleave={resetTilt}
    bind:this={tiltEl}
    style="transform-style: preserve-3d;">

    <!-- Header -->
    <div class="flex items-center gap-2">
      <div class="relative trophy w-8 h-8 flex items-center justify-center">
        <i class="fa-regular fa-trophy text-[gold] text-xl" aria-hidden="true"></i>
        <span class="sr-only">Breakthroughs</span>
        <div class="shine" aria-hidden="true"></div>
      </div>
      <div class="text-off-white font-bold montserrat text-lg tracking-wide">
        Breakthroughs
      </div>
    </div>

    <!-- Donut Galaxy -->
    <div class="mx-auto w-[200px] h-[200px] grid place-items-center relative">
      <div class="absolute inset-0 grid place-items-center">
        <div class="orbit orbit-1"></div>
        <div class="orbit orbit-2"></div>
      </div>
      <div class="donut relative w-[160px] h-[160px] rounded-full"
        style={`background: ${donutStyle}`}
        aria-label="Breakthrough distribution donut">
        <!-- inner hole -->
        <div class="absolute inset-[32px] bg-black/60 rounded-full backdrop-blur-sm border border-white/10"></div>
        <!-- glow pulse if gold leads -->
        {#if largestLabel === 'gold' && revealed}
          <div class="absolute inset-0 rounded-full gold-glow"></div>
        {/if}
      </div>
      <!-- Center label -->
      <div class="absolute text-center text-xs text-off-white/80">
        <div class="font-semibold">Total</div>
        <div class="text-2xl font-bold">{total}</div>
      </div>
    </div>

    <!-- Medal counters -->
    <div class="flex flex-col gap-2">
      <div class="counter-chip">
        <div class="chip-icon text-[gold]"><i class="fa-regular fa-trophy"></i></div>
        <div class="chip-label">Gold</div>
        <div class="chip-value">{displayGold}</div>
      </div>
      <div class="counter-chip">
        <div class="chip-icon text-[silver]"><i class="fa-regular fa-trophy"></i></div>
        <div class="chip-label">Silver</div>
        <div class="chip-value">{displaySilver}</div>
      </div>
      <div class="counter-chip">
        <div class="chip-icon text-[#CD7F32]"><i class="fa-regular fa-trophy"></i></div>
        <div class="chip-label">Bronze</div>
        <div class="chip-value">{displayBronze}</div>
      </div>
    </div>
  </div>

  <!-- Confetti burst -->
  {#if showConfetti}
    <div class="absolute inset-0 pointer-events-none">
      {#each confettiPieces as c}
        <div class="confetti" style={`--x:${c.x}%; --y:${c.y}%; --dx:${c.dx}px; --dy:${c.dy}px; --rot:${c.rot}deg; --h:${c.h}; --s:${c.s}%;`}></div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .vignette { box-shadow: inset 0 0 120px rgba(0,0,0,0.6); }

  /* starfield via multiple box-shadows */
  .stars::before {
    content: "";
    position: absolute; inset: 0;
    background: transparent;
    box-shadow:
      10px 20px 0 0 rgba(255,255,255,0.15),
      60px 40px 0 0 rgba(255,255,255,0.10),
      120px 80px 0 0 rgba(255,255,255,0.12),
      160px 30px 0 0 rgba(255,255,255,0.08),
      200px 110px 0 0 rgba(255,255,255,0.10),
      260px 70px 0 0 rgba(255,255,255,0.14);
  }

  /* Trophy shine sweep */
  .trophy { overflow: hidden; }
  .shine {
    position: absolute; inset: 0; pointer-events: none;
    background: linear-gradient(120deg, transparent 20%, rgba(255,255,255,0.6) 35%, transparent 50%);
    transform: translateX(-100%);
    animation: shine-sweep 1200ms ease-out both;
    opacity: 0.7;
  }
  @keyframes shine-sweep {
    0% { transform: translateX(-120%); opacity: 0; }
    15% { opacity: 0.7; }
    60% { opacity: 0.5; }
    100% { transform: translateX(120%); opacity: 0; }
  }

  /* Orbit rings */
  .orbit { position: absolute; border-radius: 9999px; border: 1px dashed rgba(255,255,255,0.18); }
  .orbit-1 { width: 180px; height: 180px; animation: orbit-rotate 12s linear infinite; }
  .orbit-2 { width: 210px; height: 210px; animation: orbit-rotate 18s linear infinite reverse; }
  @keyframes orbit-rotate { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }

  /* Donut glow pulse when gold leads */
  .gold-glow { box-shadow: 0 0 18px 6px rgba(255,215,0,0.45) inset, 0 0 28px rgba(255,215,0,0.35); animation: glow-pulse 2.2s ease-in-out infinite; }
  @keyframes glow-pulse { 0%, 100% { opacity: 0.8; } 50% { opacity: 1; } }

  /* Counter chips */
  .counter-chip { display: grid; grid-template-columns: 24px 1fr auto; align-items: center; gap: 8px; padding: 8px 10px; background: rgba(0,0,0,0.45); border: 1px solid rgba(255,255,255,0.12); border-radius: 9999px; backdrop-filter: blur(6px); }
  .chip-icon { display: grid; place-items: center; }
  .chip-label { font-size: 12px; color: rgba(255,255,255,0.85); }
  .chip-value { font-weight: 700; font-size: 14px; color: #fff; }

  /* Confetti */
  .confetti {
    position: absolute;
    width: 6px; height: 10px;
    left: calc(var(--x) * 1%); top: calc(var(--y) * 1%);
    background: hsl(var(--h), var(--s), 60%);
    transform: translate(calc(var(--dx) * 1px), calc(var(--dy) * 1px)) rotate(var(--rot));
    animation: confetti-fall 900ms ease-out forwards;
    border-radius: 2px;
  }
  @keyframes confetti-fall {
    0% { opacity: 1; transform: translate(0,0) rotate(0deg); }
    100% { opacity: 0; transform: translate(var(--dx), var(--dy)) rotate(var(--rot)); }
  }
</style>
